<template>
  <Modal
    :title="`Create multi-player markets`"
    :visible="isVisible"
    :class="[
      'add-combo-markets-modal',
      { 'has-error': !!formErrors.length },
    ]"
    no-close-btn
    @close="onModalClose"
  >
    <div
      :class="[
        'combo-markets-modal-content',
      ]"
    >
      <div
        v-if="!!formErrors.length"
        class="error-messages-section"
      >
        <div
          v-for="error in formErrors"
          :key="error"
          class="error-message"
        >
          {{ error }}
        </div>
      </div>
      <div class="combo-markets-modal--input-root">
        <div class="combo-markets-modal--input-root__label">
          Select multi-player market type
        </div>
        <Dropdown
          class="combo-type-dropdown"
          :placeholder="selectedComboType?.label || 'Select combo type'"
        >
          <DropdownItem
            v-for="item in availableComboTypes"
            :key="item.value"
            @click="onComboTypeSelect(item)"
            clickable
          >
            {{ item.label }}
          </DropdownItem>
        </Dropdown>
      </div>
      <div class="players-select-section">
        <DropdownWithTabs
          label="Select player 1"
          placeholder="Name Surname"
          :model-value="playerOneSelectedValue"
          :tab-items="teamTabsItems"
          :dropdown-items="playerSelectDropdownItems"
          @update:modelValue="onPlayerOneSelect"
        />
        <DropdownWithTabs
          label="Select player 2"
          placeholder="Name Surname"
          :model-value="playerTwoSelectedValue"
          :tab-items="teamTabsItems"
          :dropdown-items="playerSelectDropdownItems"
          @update:modelValue="onPlayerTwoSelect"
        />
      </div>
      <div class="combo-markets-modal--input-root">
        <div class="combo-markets-modal--input-root__label">
          Select markets
        </div>
        <Dropdown
          class="markets-dropdown"
          :placeholder="selectedMarketsPlaceholder"
          :disabled="!playerOneSelectedValue || !playerTwoSelectedValue"
        >
          <div
            v-if="!availableComboMarkets.length"
            class="no-dropdown-items-message"
          >
            No markets available for selected players
          </div>
          <DropdownItem
            v-for="item in availableComboMarkets"
            :key="item.marketCode"
            @click="onAvailableMarketToggle(item)"
            :disabled="!!item.isDisabled"
            clickable
          >
            <Checkbox
              :model-value="item.isSelected"
              :disabled="item.isDisabled"
              @update:modelValue="onAvailableMarketToggle(item)"
            />
            {{ item.marketName }}
          </DropdownItem>
        </Dropdown>
      </div>
      <Button
        @click="createComboMarket"
        :icon="'plus-circle'"
        class="create-combo-markets-btn"
        :disabled="!!isCreateComboBtnDisabled || !!formErrors.length"
      >
        Create new combo market
      </Button>
      <div
        v-if="allComboMarkets.length"
        class="created-combo-markets-section"
      >
        <div class="divider" />
        <div class="created-combo-markets-section--title">
          Created combo markets ({{ allComboMarkets.length }})
        </div>
        <div class="created-combo-markets-section--list">
          <div
            v-for="(item) in allComboMarkets"
            :key="item.comboMarketId"
            :class="[
              'created-combo-markets-section--list--item',
              { 'already-submitted': item.alreadySubmitted },
            ]"
          >
            <div
              v-if="!item.alreadySubmitted"
              class="created-combo-markets-section--list--item--actions"
            >
              <Icon
                name="trash"
                @click="deleteCreatedComboMarket(item)"
              />
              <Icon
                name="edit"
                @click="editCreatedComboMarket(item)"
              />
            </div>
            <div
              v-else
              class="created-combo-markets-section--list--item--actions already-submitted"
            >
              Already submitted
            </div>
            <div class="created-combo-markets-section--list--item--section">
              <span>Player 1:</span>
              {{ item.playerOne.label }}
            </div>
            <div class="created-combo-markets-section--list--item--section">
              <span>Player 2:</span>
              {{ item.playerTwo.label }}
            </div>
            <div class="created-combo-markets-section--list--item--section">
              <span>Markets:</span>
              {{ join(map(item.markets, (market) => market.marketName), ', ') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="combo-markets-modal-footer">
      <Button
        @click="onModalClose"
      >
        Dismiss
      </Button>
      <Button
        variant="primary"
        @click="saveComboMarkets"
        :disabled="!createdComboMarkets.length"
      >
        Save
      </Button>
    </div>
  </Modal>
</template>

<script>

import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import {
  map, filter, includes, join,
  forEach, reduce, find, some,
} from 'lodash';
import Modal from '@/components/common/Modal';
import Button from '@/components/common/Button';
import DropdownWithTabs from '@/components/common/DropdownWithTabs';
import Dropdown from '@/components/common/Dropdown';
import DropdownItem from '@/components/common/DropdownItem';
import Checkbox from '@/components/common/Checkbox';
import Icon from '@/components/common/Icon';
import { formatPlayerName } from '@/services/helpers/player-setup-mapper';

export default {
  components: {
    Modal,
    Button,
    DropdownWithTabs,
    Dropdown,
    DropdownItem,
    Checkbox,
    Icon,
  },
  emits: [
    'onClose',
    'onAddSelectedPlayers',
    'onSelectedTabChange',
  ],
  props: {
    isVisible: {
      type: Boolean,
      default: () => false,
    },
  },
  setup(props, { emit }) {
    const store = useStore();
    const playerSetupData = computed(() => store.getters.playerSetupData);
    const playerSetupTableData = computed(() => store.getters.playerSetupTableData);
    const allPlayerSetupTableDataPlayersNames = computed(() => store.getters.allPlayerSetupTableDataPlayersNames);
    const teamTabsItems = computed(() => ([
      {
        label: playerSetupData.value?.teamA?.name,
        value: playerSetupData.value?.teamA?.teamId,
      },
      {
        label: playerSetupData.value?.teamB?.name,
        value: playerSetupData.value?.teamB?.teamId,
      },
    ]));
    const playerSelectDropdownItems = computed(() => ({
      [playerSetupData.value?.teamA?.teamId]: map(
        filter(playerSetupTableData.value?.teamA, ({ playerLocked }) => playerLocked), (item) => ({
          label: formatPlayerName(item.playerName),
          value: item.playerIndex,
          marketCodeDetails: filter(item.marketCodeDetails, (market) => !market.isLocked),
        }),
      ),
      [playerSetupData.value?.teamB?.teamId]: map(
        filter(playerSetupTableData.value?.teamB, ({ playerLocked }) => playerLocked), (item) => ({
          label: formatPlayerName(item.playerName),
          value: item.playerIndex,
          marketCodeDetails: filter(item.marketCodeDetails, (market) => !market.isLocked),
        }),
      ),
    }));
    const availableComboTypes = computed(() => ([
      {
        label: 'Combo markets',
        value: 'combo-markets',
      },
      {
        label: 'Head to head markets',
        value: 'head-to-head-markets',
      },
    ]));
    const selectedComboType = ref(availableComboTypes.value[0]);
    const playerOneSelectedValue = ref(null);
    const playerTwoSelectedValue = ref(null);
    const selectedMarketsToCombine = ref([]);
    const selectedMarketsPlaceholder = computed(() => (selectedMarketsToCombine.value.length
      ? `${selectedMarketsToCombine.value.length} markets selected`
      : 'Select market'));

    const createdComboMarkets = ref([]);
    const alreadySubbmitedComboMarkets = computed(() => reduce(
      playerSetupTableData.value.playerComboMarketCodeDetails,
      (allMarkets, currentMarket, key) => {
        // Extract player indexes from the key
        const indices = key.match(/\((-?\d+),\s*(-?\d+)\)/);
        // eslint-disable-next-line no-unused-vars
        const [_, index1, index2] = indices;
        const indexesKey = `(${index1}, ${index2})`;

        return [
          ...allMarkets,
          ...[{
            comboMarketId: indexesKey,
            playerOne: {
              value: parseInt(index1, 10),
              label: formatPlayerName(allPlayerSetupTableDataPlayersNames.value[index1]),
            },
            playerTwo: {
              value: parseInt(index2, 10),
              label: formatPlayerName(allPlayerSetupTableDataPlayersNames.value[index2]),
            },
            markets: currentMarket,
            alreadySubmitted: true,
          },
          ]];
      },
      [],
    ));
    const allComboMarkets = computed(() => [
      ...(createdComboMarkets.value || []),
      ...(alreadySubbmitedComboMarkets.value || []),
    ]);

    const availableComboMarkets = computed(() => {
      if (!playerOneSelectedValue.value || !playerTwoSelectedValue.value) {
        return [];
      }
      const comboMarketsFiltered = filter(
        store.getters.availableComboMarkets,
        (market) => (
          (selectedComboType.value.value === 'combo-markets' && includes(market.marketCode, 'COMBO'))
          || (selectedComboType.value.value === 'head-to-head-markets' && includes(market.marketCode, 'HEAD_TO_HEAD'))
        ),
      );
      const marketsToDisable = map(
        find(alreadySubbmitedComboMarkets.value, (item) => (
          (item.playerOne.value === playerOneSelectedValue.value.value
        && item.playerTwo.value === playerTwoSelectedValue.value.value)
        || (item.playerOne.value === playerTwoSelectedValue.value.value
        && item.playerTwo.value === playerOneSelectedValue.value.value)
        ))?.markets, ({ marketCode }) => marketCode,
      );
      return map(
        filter(
          comboMarketsFiltered,
          (item) => includes(map(playerOneSelectedValue.value.marketCodeDetails, 'marketCode'), item.playerMarketConnectionCode)
          && includes(map(playerTwoSelectedValue.value.marketCodeDetails, 'marketCode'), item.playerMarketConnectionCode),
        ), (item) => ({
          ...item,
          isSelected: includes(map(selectedMarketsToCombine.value, 'marketCode'), item.marketCode),
          isDisabled: includes(marketsToDisable, item.marketCode),
        }),
      );
    });
    const onAvailableMarketToggle = (item) => {
      if (includes(map(selectedMarketsToCombine.value, 'marketCode'), item.marketCode)) {
        selectedMarketsToCombine.value = filter(selectedMarketsToCombine.value, (market) => market.marketCode !== item.marketCode);
      } else {
        selectedMarketsToCombine.value.push(item);
      }
    };
    const clearComboMarketForm = () => {
      playerOneSelectedValue.value = null;
      playerTwoSelectedValue.value = null;
      selectedMarketsToCombine.value = [];
    };
    const onPlayerOneSelect = (value) => {
      playerOneSelectedValue.value = value;
    };
    const onPlayerTwoSelect = (value) => {
      playerTwoSelectedValue.value = value;
    };
    const onComboTypeSelect = (value) => {
      clearComboMarketForm();
      selectedComboType.value = value;
    };

    const isCreateComboBtnDisabled = computed(() => {
      if (
        !playerOneSelectedValue.value
        || !playerTwoSelectedValue.value
        || !selectedMarketsToCombine.value.length) {
        return true;
      }
      return false;
    });

    const createComboMarket = () => {
      createdComboMarkets.value.push({
        comboMarketId: Math.random().toString(36).substring(2, 15),
        playerOne: playerOneSelectedValue.value,
        playerTwo: playerTwoSelectedValue.value,
        markets: selectedMarketsToCombine.value,
      });
      clearComboMarketForm();
    };

    const deleteCreatedComboMarket = (item) => {
      createdComboMarkets.value = filter(createdComboMarkets.value, (market) => market.comboMarketId !== item.comboMarketId);
    };
    const editCreatedComboMarket = (item) => {
      playerOneSelectedValue.value = item.playerOne;
      playerTwoSelectedValue.value = item.playerTwo;
      selectedMarketsToCombine.value = item.markets;
      deleteCreatedComboMarket(item);
    };

    const onModalClose = () => {
      clearComboMarketForm();
      emit('onClose');
    };
    const saveComboMarkets = () => {
      const playerComboMarketCodeDetails = {};
      forEach(createdComboMarkets.value, (item) => {
        let key = '';
        if (item.playerOne.value < item.playerTwo.value) {
          key = `(${item.playerOne.value}, ${item.playerTwo.value})`;
        } else {
          key = `(${item.playerTwo.value}, ${item.playerOne.value})`;
        }
        const marketsWithSameKey = playerSetupTableData.value.playerComboMarketCodeDetails[key];
        playerComboMarketCodeDetails[key] = [
          ...(playerComboMarketCodeDetails[key] || []),
          ...(marketsWithSameKey || []),
          ...map(item.markets, (market) => ({
            marketCode: market.marketCode,
            marketName: market.marketName,
            playerMarketConnectionCode: market.playerMarketConnectionCode,
            suspended: true,
            marketNotSubmitted: true,
            isLocked: true,
            isComboMarket: true,
          })),
        ];
      });
      store.dispatch('setPlayerSetupTableData', {
        ...playerSetupTableData.value,
        playerComboMarketCodeDetails: {
          ...playerSetupTableData.value.playerComboMarketCodeDetails,
          ...playerComboMarketCodeDetails,
        },
      });
      onModalClose();
    };
    const formErrors = computed(() => {
      const errors = [];
      if (!playerOneSelectedValue.value && !playerTwoSelectedValue.value) return errors;
      if (playerOneSelectedValue.value === playerTwoSelectedValue.value) {
        errors.push('Cannot select the same player');
      }
      const isAlreadyAdded = find(
        allComboMarkets.value,
        (item) => (
          ((item.playerOne.value === playerOneSelectedValue.value?.value
          && item.playerTwo.value === playerTwoSelectedValue.value?.value)
          || (item.playerOne.value === playerTwoSelectedValue.value?.value
          && item.playerTwo.value === playerOneSelectedValue.value?.value))
          && (some(
            map(item.markets, 'marketCode'),
            (marketCode) => includes(map(selectedMarketsToCombine.value, 'marketCode'), marketCode),
          ))
        ),
      );
      if (isAlreadyAdded) {
        errors.push('Combo market already created');
      }
      return errors;
    });
    return {
      teamTabsItems,
      playerSelectDropdownItems,
      playerOneSelectedValue,
      playerTwoSelectedValue,
      availableComboMarkets,
      selectedMarketsToCombine,
      selectedMarketsPlaceholder,
      isCreateComboBtnDisabled,
      createdComboMarkets,
      map,
      join,
      formErrors,
      allComboMarkets,
      onAvailableMarketToggle,
      onPlayerOneSelect,
      onPlayerTwoSelect,
      onModalClose,
      createComboMarket,
      saveComboMarkets,
      editCreatedComboMarket,
      deleteCreatedComboMarket,
      onComboTypeSelect,
      availableComboTypes,
      selectedComboType,
    };
  },
};
</script>

<style lang="scss">
.params-setup__root {
  .modal {
    &.add-combo-markets-modal {
      &.has-error {
        .modal__dialog {
          border: 1px solid $error500;
        }
      }
      .modal__dialog {
        min-width: 550px;
      }
      .modal__header {
        height: auto;
        .modal__title {
          padding: 0;
        }
      }
      .modal__content {
        padding: 0;
        .combo-markets-modal-content {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
          .error-messages-section {
            display: flex;
            flex-direction: column;
            gap: 4px;
            .error-message {
              font-size: 10px;
              color: $error500;
            }
          }
          .combo-markets-modal--input-root {
            display: flex;
            flex-direction: column;
            gap: 4px;
            &__label {
              font-size: 10px;
              font-weight: 600;
              color: $gray800;
              text-transform: uppercase;
            }
            .markets-dropdown {
              position: relative;
              z-index: 1;
              .no-dropdown-items-message {
                padding: 16px 8px;
                font-size: 12px;
                color: $gray800;
                text-align: center;
              }
              .dropdown-item {
                display: flex;
                align-items: center;
                gap: 4px;
              }
            }
          }
          .players-select-section {
            display: flex;
            align-items: center;
            gap: 16px;
            position: relative;
            z-index: 2;
            .dropdown-with-toggle-root {
              width: 100%;
            }
          }
          .create-combo-markets-btn {
            justify-content: center;
          }
          .created-combo-markets-section {
            display: flex;
            flex-direction: column;
            gap: 16px;
            .divider {
              width: 100%;
              height: 1px;
              background-color: $gray400;
            }
            &--title {
              font-size: 10px;
              font-weight: 600;
              color: $gray800;
              text-transform: uppercase;
            }
            &--list {
              display: flex;
              flex-direction: column;
              gap: 8px;
              max-height: 240px;
              padding: 2px 0;
              overflow-y: auto;
              &--item {
                display: flex;
                flex-direction: column;
                gap: 4px;
                padding: 8px;
                background-color: $gray300;
                font-size: 12px;
                position: relative;
                &--section {
                  display: flex;
                  flex-direction: column;
                  span {
                    font-weight: 600;
                    color: $gray700;
                    font-size: 8px;
                    text-transform: uppercase;
                  }
                }
                &--actions {
                  display: none;
                  align-items: center;
                  gap: 8px;
                  background-color: $white;
                  padding: 4px;
                  border-radius: 4px;
                  position: absolute;
                  top: 8px;
                  right: 8px;
                  &.already-submitted {
                    display: flex;
                    font-size: 8px;
                    color: $primary400;
                    font-weight: 600;
                    text-transform: uppercase;
                  }
                  .icon {
                    width: 20px;
                    height: 20px;
                    cursor: pointer;
                    &:first-child {
                      svg {
                        path {
                          fill: $gray800;
                        }
                      }
                    }
                    &:last-child {
                      svg {
                        path {
                          stroke: $gray800;
                          fill: $white;
                        }
                      }
                    }
                  }
                }
                &:hover {
                  .created-combo-markets-section--list--item--actions {
                    display: flex;
                  }
                }
              }
            }
          }
        }
        .combo-markets-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 16px;
          border-top: 1px solid $gray400;
        }
      }
    }
  }
}
</style>
